import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { controlFocus, minTarget } from '../primitives/shared.js';

export type ButtonVariant =
  'primary' | 'secondary' | 'tertiary' | 'destructive' | 'icon' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  /**
   * Why the action is unavailable. 07-11: a disabled control "remains in the
   * accessibility tree and remains focusable, with the reason available as
   * accessible text" — a control removed from the tab order cannot deliver it.
   */
  disabledReason?: string;
  /** Required for `icon` variant, which has no text content. */
  'aria-label'?: string;
  children: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'border-0 bg-primary text-inverse hov:bg-primary-hover active:bg-primary-active',
  secondary:
    'border border-line bg-secondary text-default hov:bg-secondary-hover active:bg-secondary-hover',
  tertiary: 'border-0 bg-transparent text-link hov:bg-sunken active:bg-secondary-hover',
  destructive:
    'border-0 bg-destructive text-inverse hov:bg-destructive-hover active:bg-destructive-hover',
  icon: 'border-0 bg-transparent text-default hov:bg-sunken active:bg-secondary-hover',
  /** @deprecated Use `tertiary`. */
  ghost: 'border-0 bg-transparent text-primary hov:bg-sunken active:bg-secondary-hover',
  /** @deprecated Use `destructive`. */
  danger: 'border-0 bg-destructive text-inverse hov:bg-destructive-hover',
};

/** PRM-01 Button — trigger an action. Never used to navigate. */
export function Button({
  variant = 'primary',
  loading = false,
  disabledReason,
  children,
  className,
  disabled,
  type = 'button',
  onClick,
  'aria-busy': ariaBusy,
  ...props
}: ButtonProps) {
  const inoperable = Boolean(disabled) || loading;

  return (
    <button
      type={type}
      data-primitive="PRM-01"
      aria-disabled={inoperable || undefined}
      aria-busy={loading || ariaBusy}
      onClick={(event) => {
        if (inoperable) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      className={cn(
        'inline-flex items-center justify-center gap-inline rounded-control px-field text-body font-medium',
        minTarget,
        variant === 'icon' ? 'aspect-square px-0' : undefined,
        controlFocus,
        VARIANTS[variant],
        inoperable && 'cursor-not-allowed opacity-60',
        className,
      )}
      {...props}
    >
      {loading ? 'Loading…' : children}
      {disabled && disabledReason ? <span className="sr-only"> — {disabledReason}</span> : null}
    </button>
  );
}
